/**
 * Junto Frontend Input Component
 * 
 * This is the base input component that all Junto frontend systems use.
 * It maintains exact visual equivalence to the current Sol Room input styles
 * while being configurable for other studios.
 */

import React from 'react';
import styled from 'styled-components';
import { solRoomTheme } from '../../themes/sol-room.js';

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm};
`;

const Label = styled.label`
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  font-weight: 500;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
`;

const StyledInput = styled.input`
  padding: ${props => props.theme?.spacing?.md || solRoomTheme.spacing.md};
  border: 1px solid ${props => props.theme?.colors?.neutral?.medium || solRoomTheme.colors.neutral.medium};
  border-radius: 8px;
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  font-size: 1rem;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  background: ${props => props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background};
  transition: ${props => props.theme?.transitions?.default || solRoomTheme.transitions.default};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main};
    box-shadow: 0 0 0 2px ${props => (props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main) + '20'};
  }
  
  &::placeholder {
    color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
    opacity: 0.5;
  }
  
  &:disabled {
    background: ${props => props.theme?.colors?.neutral?.light || solRoomTheme.colors.neutral.light};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main};
  font-size: 0.85rem;
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
`;

const HintMessage = styled.div`
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  font-size: 0.8rem;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  opacity: 0.6;
  line-height: 1.3;
`;

const Input = ({ 
  label, 
  error, 
  hint, 
  theme, 
  ...props 
}) => {
  return (
    <InputContainer theme={theme}>
      {label && <Label theme={theme}>{label}</Label>}
      <StyledInput theme={theme} {...props} />
      {error && <ErrorMessage theme={theme}>{error}</ErrorMessage>}
      {hint && !error && <HintMessage theme={theme}>{hint}</HintMessage>}
    </InputContainer>
  );
};

export default Input;